import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, CheckCircle, CalendarClock } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import BookCall from "../components/common/BookCall";
import settings from "../data/settings.json";
import useLang from "../hooks/useLang";

const Contact = () => {
  const { t, isRTL, dir } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const mode = searchParams.get("mode") === "call" ? "call" : "message";
  // "Ask about this" on the Solutions page opens the form with the solution filled in;
  // the "My challenge is" box passes the visitor's text in router state.
  const [formData, setFormData] = useState(() => {
    const list = t("contact.form.services", { returnObjects: true });
    const index = Number(searchParams.get("service"));
    const topic = searchParams.get("topic");
    return {
      name: "",
      email: "",
      service: Number.isInteger(index) && list[index] ? list[index] : "",
      message: location.state?.message || (topic ? `${topic}: ` : ""),
    };
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (e.target.botcheck?.checked) return;
    setError("");
    setSending(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          subject: `New message from ${formData.name} — Applumo website`,
          from_name: "Applumo Website",
          name: formData.name,
          email: formData.email,
          service: formData.service || "—",
          message: formData.message,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message);
      setSent(true);
      setFormData({ name: "", email: "", service: "", message: "" });
    } catch {
      setError(t("contact.form.error"));
    } finally {
      setSending(false);
    }
  };

  const services = t("contact.form.services", { returnObjects: true });

  const contactInfo = [
    { icon: Mail, label: t("contact.info.email"), value: settings.company.email, href: `mailto:${settings.company.email}` },
    { icon: Phone, label: t("contact.info.phone"), value: settings.company.phone, href: `tel:${settings.company.phone}` },
    { icon: MapPin, label: t("contact.info.location"), value: isRTL ? settings.company.addressAr : settings.company.address, href: settings.company.mapUrl, external: true },
  ];

  return (
    <div dir={dir} className="font-inter">
      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container-site text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-block bg-accent/10 text-accent text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
              {t("contact.badge")}
            </span>
            <h1 className="text-5xl font-bold text-gray-900 mb-5">{t("contact.title")}</h1>
            <p className="text-gray-500 text-xl max-w-xl mx-auto">{t("contact.subtitle")}</p>
          </motion.div>
        </div>
      </section>

      {/* Main */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="inline-flex p-1 rounded-xl bg-zinc-100 mb-8" role="tablist">
                {["message", "call"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={mode === m}
                    onClick={() => setSearchParams(m === "call" ? { mode: "call" } : {}, { replace: true })}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                      mode === m ? "bg-white text-ink shadow-sm" : "text-zinc-500 hover:text-ink"
                    }`}
                  >
                    {m === "call" ? <CalendarClock size={16} /> : <Mail size={16} />}
                    {t(`contact.mode.${m}`)}
                  </button>
                ))}
              </div>

              {mode === "call" ? (
                <BookCall />
              ) : (
              <>
              {sent ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle size={40} className="text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t("contact.form.sentTitle")}</h3>
                  <p className="text-gray-500">{t("contact.form.success")}</p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-8 text-sm text-accent hover:underline"
                  >
                    {t("contact.form.sendAnother")}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <input type="checkbox" name="botcheck" className="hidden" tabIndex={-1} autoComplete="off" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t("contact.form.name")} *
                      </label>
                      <input
                        required
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t("contact.form.email")} *
                      </label>
                      <input
                        required
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("contact.form.service")}
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent bg-white transition-colors"
                    >
                      <option value="">{t("contact.form.selectService")}</option>
                      {services.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("contact.form.message")} *
                    </label>
                    <textarea
                      required
                      rows={5}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t("contact.form.messagePlaceholder")}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-none transition-colors"
                    />
                  </div>
                  {error && (
                    <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-primary text-white font-semibold py-3.5 rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-60 text-sm"
                  >
                    {sending ? t("contact.form.sending") : t("contact.form.send")}
                  </button>
                </form>
              )}
              </>
              )}
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              {/* Contact details in a frosted panel over the office map (Google Maps embed, no API key). */}
              <div className="relative overflow-hidden rounded-3xl border border-zinc-200 bg-zinc-100 min-h-[540px]">
                {/* Taller than the card and shifted up so the pin sits in the open top area, not under the panel. */}
                <iframe
                  title={t("contact.info.mapTitle")}
                  src={`https://www.google.com/maps/embed?origin=mfe&pb=!1m3!2m1!1s${settings.company.mapCoords}!6i12!3m1!1s${isRTL ? "ar" : "en"}!5m1!1s${isRTL ? "ar" : "en"}`}
                  className="absolute inset-x-0 top-[-50%] h-[150%] w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <div className="absolute inset-x-4 bottom-4 rounded-2xl bg-ink/85 p-6 text-white backdrop-blur-md shadow-[0_24px_60px_-20px_rgba(10,10,10,0.7)]">
                  <h3 className="text-lg font-bold mb-5">{t("contact.info.title")}</h3>
                  <div className="space-y-4">
                    {contactInfo.map(({ icon: Icon, label, value, href, external }) => (
                      <a
                        key={label}
                        href={href}
                        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="flex items-start gap-4 group"
                      >
                        <div className="w-10 h-10 bg-rose/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-rose/30 transition-colors">
                          <Icon size={16} className="text-rose-light" />
                        </div>
                        <div>
                          <p className="text-white/50 text-xs mb-0.5">{label}</p>
                          <p className="text-white text-sm font-medium">{value}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                  <a
                    href={settings.company.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-rose-light hover:text-white transition-colors"
                  >
                    <MapPin size={15} /> {t("contact.info.openMap")} ↗
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
